import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  GlobeAltIcon,
  LinkIcon,
  LockClosedIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import {
  artifactShareUrl,
  type ArtifactVisibility,
  useDeleteCompanyArtifactMutation,
  useGetCompanyArtifactContentQuery,
  useGetCompanyArtifactQuery,
  useUpdateCompanyArtifactMutation,
} from "~/ui-rtk/api/companyArtifactApi"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"
import { H1, H3 } from "~/ui-rtk/components/ui/typography"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import useAccess from "~/ui-rtk/hooks/access"
import useDate from "~/ui-rtk/hooks/date"
import ArtifactFrame from "./ArtifactFrame"
import PublishArtifactDialog from "./PublishArtifactDialog"

const dayjs = useDate()

/**
 * One artifact, full height. Everyone sees the current version; the agency
 * (super admins) can preview any version, make it current (rollback), publish
 * a new version, or delete.
 */
export default function ViewArtifactPage() {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const { isSuperAdmin } = useAccess()
  const {
    data: artifact,
    isLoading,
    error,
  } = useGetCompanyArtifactQuery(id, { skip: !id })

  // The version on screen; follows the current one unless the agency picks another.
  const [viewedVersion, setViewedVersion] = useState<number | null>(null)
  useEffect(() => {
    setViewedVersion(artifact?.currentVersion ?? null)
  }, [artifact?.id, artifact?.currentVersion])

  const {
    data: content,
    isFetching: contentLoading,
    error: contentError,
  } = useGetCompanyArtifactContentQuery(
    { id, version: viewedVersion ?? 0 },
    { skip: !viewedVersion },
  )

  const [updateArtifact, { isLoading: restoring }] =
    useUpdateCompanyArtifactMutation()
  const [updateVisibility, { isLoading: changingVisibility }] =
    useUpdateCompanyArtifactMutation()
  const [deleteArtifact, { isLoading: deleting }] =
    useDeleteCompanyArtifactMutation()
  const [publishing, setPublishing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  if (isLoading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Loader size={50} />
      </div>
    )
  }
  if (error || !artifact) {
    return (
      <div className="px-system-md mt-10 text-system-gray-400">
        Artifact not found.
      </div>
    )
  }

  const viewed = artifact.versions?.find(v => v.version === viewedVersion)
  const isViewingCurrent = viewedVersion === artifact.currentVersion

  const handleMakeCurrent = async () => {
    if (!viewedVersion) return
    try {
      await updateArtifact({
        id,
        body: { currentVersion: viewedVersion },
      }).unwrap()
      toast.success(`Version ${viewedVersion} is now live`)
    } catch {
      toast.error("Could not switch the version. Please try again.")
    }
  }

  const handleVisibility = async (visibility: ArtifactVisibility) => {
    try {
      await updateVisibility({ id, body: { visibility } }).unwrap()
      toast.success(
        visibility === "PUBLIC"
          ? "Public: anyone with the link can open it"
          : "Private: only your company can open it; shared links stop working",
      )
    } catch {
      toast.error("Could not change who can see it. Please try again.")
    }
  }

  const handleCopyLink = async () => {
    if (!artifact.publicToken) return
    try {
      await navigator.clipboard.writeText(
        artifactShareUrl(artifact.publicToken),
      )
      toast.success("Public link copied")
    } catch {
      toast.error("Could not copy the link.")
    }
  }

  const isPublic = artifact.visibility === "PUBLIC"

  const handleDelete = async () => {
    try {
      await deleteArtifact(id).unwrap()
      toast.success("Artifact deleted")
      navigate(brandRoutes.ARTIFACTS.ROOT)
    } catch {
      toast.error("Could not delete the artifact. Please try again.")
    } finally {
      setConfirmingDelete(false)
    }
  }

  return (
    <div className="px-system-md pb-system-md flex flex-col h-[calc(100vh-4rem)]">
      <div className="mt-6 flex flex-wrap justify-between items-start gap-system-sm">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <H1 className="text-white font-bold text-2xl">{artifact.title}</H1>
            <span
              data-testid="artifact-visibility"
              className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                isPublic
                  ? "bg-emerald-600/20 text-emerald-300"
                  : "bg-white/10 text-system-gray-300"
              }`}
            >
              {isPublic ? (
                <GlobeAltIcon className="w-3.5 h-3.5" />
              ) : (
                <LockClosedIcon className="w-3.5 h-3.5" />
              )}
              {isPublic ? "Public" : "Private"}
            </span>
          </div>
          {artifact.description && (
            <p className="mt-1 text-sm text-system-gray-300">
              {artifact.description}
            </p>
          )}
          {viewed && (
            <p className="mt-1 text-xs text-system-gray-500">
              Version {viewed.version} ·{" "}
              {dayjs(viewed.createdAt).format("MMM D, YYYY HH:mm")}
              {isSuperAdmin && viewed.createdByEmail
                ? ` · ${viewed.createdByEmail}`
                : ""}
              {viewed.note ? ` · ${viewed.note}` : ""}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-system-sm">
          {isPublic && artifact.publicToken && (
            <Button
              size="sm"
              variant={{ variant: "outlined" }}
              onClick={handleCopyLink}
            >
              <span className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                Copy public link
              </span>
            </Button>
          )}
          {isSuperAdmin && (
            <Button
              size="sm"
              variant={{ variant: "outlined" }}
              onClick={() => handleVisibility(isPublic ? "PRIVATE" : "PUBLIC")}
              isLoading={changingVisibility}
            >
              <span className="flex items-center gap-2">
                {isPublic ? (
                  <LockClosedIcon className="w-4 h-4" />
                ) : (
                  <GlobeAltIcon className="w-4 h-4" />
                )}
                {isPublic ? "Make private" : "Make public"}
              </span>
            </Button>
          )}
        </div>

        {isSuperAdmin && (
          <div className="flex flex-wrap items-center gap-system-sm">
            <select
              aria-label="Version"
              className="rounded-md bg-basic-black border border-basic-blue px-2 py-1.5 text-sm text-white"
              value={viewedVersion ?? ""}
              onChange={e => setViewedVersion(Number(e.target.value))}
            >
              {(artifact.versions ?? []).map(v => (
                <option key={v.version} value={v.version}>
                  v{v.version}
                  {v.version === artifact.currentVersion ? " (live)" : ""}
                </option>
              ))}
            </select>
            {!isViewingCurrent && (
              <Button
                size="sm"
                variant={{ variant: "outlined" }}
                onClick={handleMakeCurrent}
                isLoading={restoring}
              >
                <span className="flex items-center gap-2">
                  <ArrowPathIcon className="w-4 h-4" />
                  Make live
                </span>
              </Button>
            )}
            <Button
              size="sm"
              variant={{ variant: "outlined" }}
              onClick={() => setPublishing(true)}
            >
              <span className="flex items-center gap-2">
                <ArrowUpTrayIcon className="w-4 h-4" />
                New version
              </span>
            </Button>
            <Button
              size="sm"
              variant={{ variant: "outlined" }}
              onClick={() => setConfirmingDelete(true)}
            >
              <span className="flex items-center gap-2">
                <TrashIcon className="w-4 h-4 text-error" />
                Delete
              </span>
            </Button>
          </div>
        )}
      </div>

      <div className="mt-system-md flex-1 min-h-[400px]">
        {contentLoading && (
          <div className="h-full flex items-center justify-center">
            <Loader size={50} />
          </div>
        )}
        {!contentLoading && contentError && (
          <div className="text-system-gray-400">Could not load the page.</div>
        )}
        {!contentLoading && content && (
          <ArtifactFrame html={content.html} title={artifact.title} />
        )}
      </div>

      {isSuperAdmin && (
        <PublishArtifactDialog
          isOpen={publishing}
          onClose={() => setPublishing(false)}
          artifact={artifact}
        />
      )}

      <Dialog
        isOpen={confirmingDelete}
        handleClose={() => !deleting && setConfirmingDelete(false)}
        closeOnClickOutside
        width={420}
      >
        <Card
          rounded
          className="p-5 bg-basic-dark-blue border border-basic-blue text-left"
        >
          <div className="flex items-start justify-between gap-4">
            <H3 className="text-white font-semibold text-lg">
              Delete “{artifact.title}”?
            </H3>
            <button
              type="button"
              onClick={() => !deleting && setConfirmingDelete(false)}
              className="text-gray-400 transition-colors hover:text-white"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          <p className="text-sm text-system-gray-300 mt-2">
            The artifact disappears for everyone in this company. Its stored
            versions are kept, so an engineer can restore it.
          </p>
          <div className="mt-5 flex justify-end gap-system-sm">
            <Button
              size="sm"
              variant={{ variant: "outlined" }}
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant={{ variant: "solid", color: "pink" }}
              onClick={handleDelete}
              isLoading={deleting}
            >
              Delete artifact
            </Button>
          </div>
        </Card>
      </Dialog>
    </div>
  )
}
